import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { CandidateStatus, CompanyJobOpening } from "@/types";
import { Opportunity } from "@/queries/candidate/opportunities";

interface DeclineOfferParams {
  openingId: string;
  offerId: string;
}

export const useDeclineOffer = (
  onSuccess?: (data: CandidateStatus) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ offerId }: DeclineOfferParams) => {
      const token = await getAccessTokenSilently();

      return request<CandidateStatus>({
        method: "POST",
        path: `/api/jobOpening/offer/${offerId}/decline`,
        token,
      });
    },
    onSuccess(data, variables) {
      onSuccess?.(data);

      const candidateStatusId = data.id;

      qc.setQueryData<CompanyJobOpening>(
        QueryKeys.companyOpportunity(variables.openingId),
        (old) => {
          if (!old) {
            return old;
          }

          const candidates = old.candidates ? [...old.candidates] : [];

          const candidateIdx = candidates.findIndex(
            (candidate) => candidate.id === candidateStatusId,
          );

          if (candidateIdx === -1) {
            return old;
          }

          candidates[candidateIdx] = data;

          return {
            ...old,
            candidates,
          };
        },
      );

      qc.setQueryData<CompanyJobOpening[]>(
        QueryKeys.companyOpportunities,
        (old) => {
          if (!old) {
            return old;
          }

          const opportunities = [...old];

          const opportunity = opportunities.find(
            (opportunity) => opportunity.id === variables.openingId,
          );

          if (!opportunity) {
            return old;
          }

          const candidates = opportunity.candidates
            ? [...opportunity.candidates]
            : [];

          const candidateIdx = candidates.findIndex(
            (candidate) => candidate.id === candidateStatusId,
          );

          if (candidateIdx === -1) {
            return old;
          }

          candidates[candidateIdx] = data;
          opportunity.candidates = candidates;

          return opportunities;
        },
      );

      qc.setQueryData<Opportunity[]>(
        QueryKeys.candidateOpportunities,
        (old) => {
          if (!old) {
            return old;
          }

          const opportunities = [...old];

          const opportunity = opportunities.find(
            (opportunity) => opportunity.id === variables.openingId,
          );

          if (opportunity?.candidateStatus) {
            opportunity.candidateStatus.offers.forEach((offer) => {
              if (offer.id === variables.offerId) {
                offer.status = "rejected";
              }
            });
          }

          return opportunities;
        },
      );

      QueryKeys.candidateOpportunities;
    },
  });

  return {
    declineOffer: mutate,
    isDecliningOffer: isPending,
    declineOfferError: error,
  };
};
